import { Outlet, Link, useLocation } from 'react-router-dom'
import { TopNavigation } from '@/components/navigation/top-navigation'
import { BarChart3, Brain, Users, Plug, Archive, Settings, Building2 } from 'lucide-react'

/**
 * EnterpriseLayout - For Enterprise tier users
 * Enhanced layout with enterprise-specific navigation and styling
 */
export function EnterpriseLayout() {
  const location = useLocation()

  // Navigation items for enterprise users (more features)
  const navItems = [
    { label: 'Dashboard', href: '/enterprise/dashboard', icon: BarChart3 },
    { label: 'Intelligence', href: '/app/intelligence', icon: Brain },
    { label: 'Team', href: '/enterprise/team', icon: Users },
    { label: 'Integrations', href: '/enterprise/integrations', icon: Plug },
    { label: 'Vault', href: '/enterprise/vault', icon: Archive },
    { label: 'Settings', href: '/enterprise/settings', icon: Settings },
  ]

  const isActive = (href: string) => location.pathname.startsWith(href)

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation */}
      <TopNavigation />
      
      <div className="flex h-[calc(100vh-64px)]">
        {/* Sidebar with enterprise styling */}
        <aside className="w-64 bg-gradient-to-b from-purple-50 to-white border-r border-purple-200 overflow-y-auto">
          <nav className="p-4 space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive(item.href)
                    ? 'bg-purple-100 text-purple-700 font-medium'
                    : 'text-gray-700 hover:bg-purple-50'
                }`}
              >
                <item.icon className={`w-5 h-5 ${isActive(item.href) ? 'text-purple-600' : ''}`} />
                <span>{item.label}</span>
              </Link>
            ))}
          </nav>
          
          {/* Enterprise badge */}
          <div className="mx-4 mt-8 p-4 bg-purple-100 border border-purple-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="w-5 h-5 text-purple-600" />
              <p className="text-sm font-semibold text-purple-900">
                Enterprise
              </p>
            </div>
            <p className="text-xs text-purple-700">
              Internal intelligence platform with advanced features
            </p>
          </div>
          
          {/* Enterprise features highlight */}
          <div className="mx-4 mt-4 p-3 bg-white border border-gray-200 rounded-lg">
            <p className="text-xs font-semibold text-gray-900 mb-2">
              Enterprise Features
            </p>
            <ul className="space-y-1 text-xs text-gray-600">
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                Slack Integration
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                Email Digests
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                Team Management
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                Unlimited Users
              </li>
            </ul>
          </div>
        </aside>

        {/* Main content area */}
        <main className="flex-1 overflow-auto">
          <div className="max-w-7xl mx-auto p-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  )
}